import { TextArea } from "@blueprintjs/core";
import React, { useEffect, useState, useRef, useCallback } from "react";

const TEXTAREA_MAX_FONT = 16;
const TEXTAREA_MIN_FONT = 8;
const TEXTAREA_SHRINK_STEP = 0.5;

export function EditTextComponent(editor) {
  const { Canvaseditor } = editor;
  const [textObjects, setTextObjects] = useState([]);
  const textareaRefs = useRef({});

  useEffect(() => {
    if (Canvaseditor) {
      const updateTextObjects = () => {
        const objects = Canvaseditor.getObjects().filter(
          (obj) => obj.type === "textbox"
        );

        // Ensure each object has a unique identifier
        objects.forEach((obj) => {
          if (!obj.customId) {
            obj.customId = `text_${new Date().getTime()}_${Math.random()
              .toString(36)
              .substr(2, 9)}`;
          }
        });

        setTextObjects(
          objects.map((obj) => ({ id: obj.customId, text: obj.text }))
        );
      };

      Canvaseditor.on("object:added", updateTextObjects);
      Canvaseditor.on("mouse:down", updateTextObjects);

      updateTextObjects(); // Initial load
    }
  }, [Canvaseditor]);

  /**
   * Shrink the textarea font-size so content never scrolls.
   * Grows back toward TEXTAREA_MAX_FONT when content is removed.
   */
  const adjustTextareaFont = useCallback((id) => {
    const el = textareaRefs.current[id];
    if (!el) return;

    // Read current font size
    let fontSize = parseFloat(el.style.fontSize) || TEXTAREA_MAX_FONT;

    // Shrink until content fits or we hit minimum
    while (el.scrollHeight > el.clientHeight && fontSize > TEXTAREA_MIN_FONT) {
      fontSize = Math.max(TEXTAREA_MIN_FONT, fontSize - TEXTAREA_SHRINK_STEP);
      el.style.fontSize = fontSize + "px";
    }

    // Grow back if there's room
    while (fontSize < TEXTAREA_MAX_FONT) {
      const next = Math.min(TEXTAREA_MAX_FONT, fontSize + TEXTAREA_SHRINK_STEP);
      el.style.fontSize = next + "px";
      if (el.scrollHeight > el.clientHeight) {
        // Went too far — revert
        el.style.fontSize = fontSize + "px";
        break;
      }
      fontSize = next;
    }
  }, []);

  // Handle text updates for the specific object
  const handleTextChange = (id, value) => {
    setTextObjects((prevTextObjects) =>
      prevTextObjects.map((item) =>
        item.id === id ? { ...item, text: value } : item
      )
    );

    const targetObject = Canvaseditor.getObjects().find(
      (obj) => obj.customId === id
    );
    if (targetObject) {
      // Use set() + initDimensions so layout recalculates properly
      targetObject.set("text", value);
      targetObject.initDimensions();

      // Fire text:changed so the canvas auto-shrink handler runs
      Canvaseditor.fire("text:changed", { target: targetObject });
    }

    // Adjust the sidebar textarea font after React renders the new value
    requestAnimationFrame(() => adjustTextareaFont(id));
  };

  /**
   * Store a ref for each textarea and run an initial font fit
   * once the element mounts.
   */
  const setTextareaRef = useCallback(
    (id, el) => {
      if (el) {
        textareaRefs.current[id] = el;
        // Initial fit after mount
        requestAnimationFrame(() => adjustTextareaFont(id));
      } else {
        delete textareaRefs.current[id];
      }
    },
    [adjustTextareaFont]
  );

  return (
    <div style={{ width: "100%", float: "left" }}>
      <h4>Quick Edit Text</h4>
      {textObjects.length === 0 ? (
        <p>No text elements found on the canvas.</p>
      ) : (
        textObjects.map((item) => (
          <TextArea
            key={item.id}
            value={item.text}
            onChange={(e) => handleTextChange(item.id, e.target.value)}
            placeholder="Edit text"
            inputRef={(el) => setTextareaRef(item.id, el)}
            style={{
              width: "100%",
              marginBottom: "10px",
              height: "200px",
              fontSize: TEXTAREA_MAX_FONT + "px",
              lineHeight: "1.3",
              overflow: "hidden",
              transition: "font-size 0.15s ease",
            }}
            className="edit_text"
            large
          />
        ))
      )}
    </div>
  );
}
